import 'Partials/collapse';

import { q, ww } from 'bobjoll/ts/library/dom';
import { modal } from 'BobjollModal';
import Notifications from 'BobjollNotifications';
import { storeUrl } from 'CommonFI/urls.helper';
import { EventTracker } from 'Library/eventTacker';
import { getGlobalVariables } from 'Library/helpers/global';
import delay from 'Project/ts/library/delay';
import User from 'Project/ts/pods/user/main';
import { logoutSession, refreshUserSession } from 'Project/ts/pods/user/userSessionService';

import { PricingUpgradeData } from '.';
import { Coupon, CouponVerifyResponse } from './api/coupon';
import { Subscription } from './api/upgrade';

const notifications = new Notifications({
    fixed: true,
    template: require('bobjoll/ts/views/hbs/alert-v1.0/element.html.hbs'),
});

const eventsUpgrade = {
    displayModalUpgrade: {
        category: 'Subscription',
        action: 'Upgrade',
        label: 'Display modal add product (legacy)',
    },
    confirm: {
        category: 'Subscription',
        action: 'Upgrade',
        label: 'Confirm (legacy)',
    },
    confirm2: {
        category: 'Subscription',
        action: 'Upgrade',
        label: 'Confirm (2) (legacy)',
    },
};

const eventsAddProduct = {
    displayModalUpgrade: {
        category: 'Subscription',
        action: 'Modal',
        label: 'Display add product',
    },
    confirm: {
        category: 'Subscription',
        action: 'Modal',
        label: 'Click add product',
    },
    confirm2: {
        category: 'Subscription',
        action: 'Modal',
        label: 'Click add product (2)',
    },
};

export const initializeModalUpgrade = function(modalData: PricingUpgradeData, planData: ModalUpgradeOptions) {
    const user = User.getData();
    const template = require('Project/ts/pages/pricing/modalUpgrade.hbs');
    modal.print(
        {
            html: template({ user, modalData, planData, ...getGlobalVariables() }),
            name: 'subscription-upgrade',
            show: true,
        },
        true,
    ) as HTMLElement;
    EventTracker.setGA('currencyCode', planData.currency);
    let events = modalData.hasAnotherPremiumSubscription ? eventsAddProduct : eventsUpgrade;
    EventTracker.send(
        'ga',
        'event',
        events.displayModalUpgrade.action,
        events.displayModalUpgrade.category,
        events.displayModalUpgrade.label,
    );
    document.getElementById('coupon')?.addEventListener('submit', eventHandlerCouponSubmit);
    document.getElementById('subscription-upgrade')?.addEventListener('click', function() {
        eventHandlerSubscriptionUpgrade.call(this, modalData);
    });
    let loader = q('#loader');
    if (loader) {
        loader.style.display = 'none';
    }
    q('#coupon-response-success .notification__close')?.addEventListener('click', eventHandlerNotificationClose);
};

const eventHandlerCouponSubmit = async function(this: HTMLFormElement, event: Event) {
    event.preventDefault();
    const modalElement = this.parent('.modal') as HTMLElement | undefined;
    const collapseElement = this.parent('.collapse');
    const data = new FormData(this);
    const coupon = data.get('coupon');
    const frequency = data.get('frequency') as PricingFrequency;
    if (
        modalElement &&
        collapseElement &&
        'string' === typeof coupon &&
        'string' === typeof frequency &&
        frequency.match(/month|year/gi)
    ) {
        q('button', this)?.classList.add('button--loading');
        const response = await Coupon.verify({
            coupon,
            frequency,
        });
        const responseType = response.success ? 'success' : 'error';
        if ('success' === responseType) {
            collapseElement.classList.remove('collapse--show');
            document.querySelector('.collapse')?.classList.add('hidden');
        }
        notifications.hide(`coupon-response-error`);
        notifications.hide(`coupon-response-success`);
        await delay(100);
        notifications.insert({
            id: `coupon-response-${responseType}`,
            class: `notification--static notification--no-shadow notification--${responseType}`,
            html: response.success
                ? `${messages.pricing.coupon_applied} ${coupon.toUpperCase()}. ${messages.pricing.coupon_use_only}`
                : messages.pricing.coupon_invalid,
            insert: {
                element: collapseElement,
                position: 'afterend',
            },
        });
        q('#coupon-response-success .notification__close')?.addEventListener('click', eventHandlerNotificationClose);
        q('button', this)?.classList.remove('button--loading');
        updatePriceElement(response);
    }
};

const eventHandlerSubscriptionUpgrade = async function(this: HTMLButtonElement, modalData: PricingUpgradeData) {
    const couponFormElement = document.getElementById('coupon') as HTMLFormElement | undefined;
    if (couponFormElement) {
        const data = new FormData(couponFormElement);
        const discountCode = data.get('coupon') as string;
        const frequency = data.get('frequency') as PricingFrequency;
        this.classList.add('button--loading');
        let events = modalData.hasAnotherPremiumSubscription ? eventsAddProduct : eventsUpgrade;
        EventTracker.send('ga', 'event', events.confirm.action, events.confirm.category, events.confirm.label);
        EventTracker.send('ga', 'event', events.confirm2.action, events.confirm2.category, events.confirm2.label);
        await Subscription.upgrade({
            discountCode,
            frequency,
        })
            .then(() => {
                refreshUserSession();

                window.location.href = `${ACCOUNTS_BASE_URL}my_subscriptions`;
            })
            .catch(() => {
                storeUrl(GOOGLE_IDP_URL_LOGIN);
                logoutSession();
                window.location.href = GOOGLE_IDP_URL_LOGIN;
            });
        this.classList.remove('button--loading');
    } else {
        storeUrl(GOOGLE_IDP_URL_LOGIN);
        logoutSession();
        window.location.href = GOOGLE_IDP_URL_LOGIN;
    }
};

const eventHandlerNotificationClose = function(this: HTMLButtonElement) {
    updatePriceElement();
    q(`#coupon-response-success`)?.remove();
    if (document.querySelector('.collapse')?.classList.contains('hidden')) {
        document.querySelector('.collapse')?.classList.remove('hidden');
    }
    this.removeEventListener('click', eventHandlerNotificationClose);
};

const updatePriceElement = (response?: CouponVerifyResponse) => {
    const modalElement = document.getElementById('modal-subscription-upgrade');
    if (modalElement) {
        const priceElement = q('.box .price', modalElement);
        const priceWithoutTaxesElement = q('.box .price-without-taxes', modalElement);
        const taxesAmountElement = q('.box .taxes-amount', modalElement);
        if (priceElement) {
            if (response) {
                priceElement.textContent = response.success ? response.data.price : priceElement.dataset.price || '';
                if (priceWithoutTaxesElement && taxesAmountElement) {
                    priceWithoutTaxesElement.textContent = response.success
                        ? response.data.priceWithoutTaxes
                        : priceWithoutTaxesElement.dataset.priceNoTaxes || '';
                    taxesAmountElement.textContent = response.success
                        ? response.data.taxesAmount
                        : taxesAmountElement.dataset.taxesAmount || '';
                }
                q('section', modalElement)?.classList[response.success ? 'add' : 'remove']('coupon-applied');
            } else {
                priceElement.textContent = priceElement.dataset.price || '';
                if (priceWithoutTaxesElement && taxesAmountElement) {
                    priceWithoutTaxesElement.textContent = priceWithoutTaxesElement.dataset.priceNoTaxes || '';
                    taxesAmountElement.textContent = taxesAmountElement.dataset.taxesAmount || '';
                }
                q('section', modalElement)?.classList.remove('coupon-applied');
            }
        }
    }
};

export interface ModalUpgradeOptions {
    coupon: string;
    frequency: PricingFrequency;
    price: string;
    priceFrequency: string;
    currency: string;
    priceNoTaxes: string;
    taxesPercentage?: string;
    taxesAmount?: string;
    priceNoDiscount: string;
    priceNoDiscountNoTaxes: string;
    taxesAmountNoDiscount: string;
}

export type PricingFrequency = 'month' | 'year';
